<script setup lang="ts">
import { ref } from 'vue';

import {
  HotelSelectionPageComponentAsyncImport,
  HotelDetailsPageComponentAsyncImport,
  FlightListPageComponentAsyncImport,
  ActivitySelectionPageComponentAsyncImport,
  BuildYourTripPageComponentAsyncImport,
  FerryListPageComponentAsyncImport,
  ActivityDetailsPageComponentAsyncImport,
} from '@apps/main/modules/router/tripPlannerRoute';

import { useBackgroundComponentsPreload } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import { AppPortableText, AppButton } from '@libs/ui';

import SharedHeader from '../../core/components/SharedHeader.vue';
import FirstDayTripPriceRibbon from '../../core/components/FirstDayTripPriceRibbon.vue';
import AITripDescription from '../../core/components/AITripDescription.vue';
import {
  useBrowserStore,
  useCustomerFlowStore,
  usePerformanceStore,
  usePremadeTripStore,
  useRouterStore,
} from '../../core/stores';
import {
  CoreEventSource,
  TemplateIds,
  PremadeTripType,
} from '../../core/models';
import { TabKey } from '../../core/stores/configurationPanel/configurationPanelStore.model';
import { CarTripPlannerButton } from '../../modules/carTripPlannerButton';

import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from './stores';
import TripSummaryColumn from './components/tripSummary/TripSummaryColumn.vue';
import TripOverviewPageLayoutDesktop from './TripOverviewPageLayoutDesktop.vue';
import TripItinerary from './components/tripItinerary/TripItinerary.vue';
import TripItineraryLoader from './components/tripItinerary/loaders/TripItineraryLoader.vue';
import GlobalSuggestionTypeSwitch from './components/globalSuggestionType/GlobalSuggestionTypeSwitch.vue';
import PremadeTripHeaderDesktop from './components/tripOverviewHeader/PremadeTripHeaderDesktop.vue';

const performanceStore = usePerformanceStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const premadeTripStore = usePremadeTripStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const browserStore = useBrowserStore();
const customerFlowStore = useCustomerFlowStore();
const routerStore = useRouterStore();
const tripSummaryStore = useTripSummaryStore();

const isLongScroll = ref<boolean>(false);

const preloadComponents = useBackgroundComponentsPreload([
  HotelSelectionPageComponentAsyncImport,
  HotelDetailsPageComponentAsyncImport,
  FlightListPageComponentAsyncImport,
  ActivitySelectionPageComponentAsyncImport,
  BuildYourTripPageComponentAsyncImport,
  FerryListPageComponentAsyncImport,
  ActivityDetailsPageComponentAsyncImport,
]);

const isCarTripPlannerUser = customerFlowStore.isCarTripFlow;

function onContentRendered(): void {
  performanceStore.trackTripOverviewPageRenderedWithContentPerformance();

  preloadComponents.start();
}

function onLongScroll(): void {
  isLongScroll.value = true;
}

function openPriceCalendar(): void {
  if (browserStore.isSmallerThan900px) {
    tripOverviewPanelsStore.openConfigurationPanel(
      TabKey.Calendar,
      'low-price-calendar'
    );
  } else {
    tripOverviewModalsStore.openTripCalendarModal();
  }
}

function openPremadeTripDetailsModal(): void {
  tripOverviewModalsStore.openPremadeTripDetailsModal();
}
</script>

<template>
  <TripOverviewPageLayoutDesktop
    @vue:mounted="performanceStore.trackTripOverviewPageRenderedPerformance()"
    @long-scroll="onLongScroll"
  >
    <template #shared-header>
      <SharedHeader
        :should-show-print-button="true"
        hide-box-shadow
        @print-view="routerStore.navigateToTripOverviewPrint()"
      />
    </template>

    <template #sticky-header>
      <div class="sticky-header-wrapper">
        <GlobalSuggestionTypeSwitch
          class="global-suggestion"
          @low-price-calendar-click="openPriceCalendar"
        />
        <AppButton
          type="text"
          state="enabled"
          size="medium"
          :wide="false"
          @click="tripSummaryStore.openShareWidget"
        >
          {{ $t('planner.share_or_save_trip') }}
        </AppButton>
        <CarTripPlannerButton v-if="!!isCarTripPlannerUser" />
      </div>
    </template>
    <template #header-content>
      <FirstDayTripPriceRibbon
        :id="TemplateIds.PriceCalendar"
        :show-month-select-dropdown="false"
        :source="CoreEventSource.TripOverview"
        scroll-track-theme="white"
      />
    </template>
    <template #leftColumn>
      <PremadeTripHeaderDesktop
        v-if="premadeTripStore.premadeTrip"
        :trip-details="premadeTripStore.premadeTrip"
        @show-full-description="openPremadeTripDetailsModal"
      >
        <template #description>
          <AppPortableText
            v-if="
              premadeTripStore.premadeTrip.type ===
              PremadeTripType.PublishedInSanity
            "
            :portable-text="premadeTripStore.premadeTrip.description"
            type="premade-trip"
            class="description"
          />
          <AITripDescription
            v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
            :description="premadeTripStore.premadeTrip.description"
          />
        </template>
      </PremadeTripHeaderDesktop>
      <TripItinerary
        v-if="
          tripOverviewPageStore.anyItineraryLoaded &&
          !isFeatureToggleActive('loader')
        "
        :print-mode="false"
        @vue:mounted="onContentRendered()"
      />
      <TripItineraryLoader v-else />
    </template>
    <template #rightColumn>
      <TripSummaryColumn />
    </template>
  </TripOverviewPageLayoutDesktop>
</template>

<style lang="scss" scoped>
.sticky-header-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>
